import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Connect } from 'redux-zero/react';
import ScreenReaderOnly from '@embed/components/common/ScreenReaderOnly';
import { EventType } from '@embed/customEmbedEvent';
import { useTranslation } from '@embed/hooks/react-i18nextLazy';
import { ensureResizeObserver } from '@src/embeds/utils/resizeObserverPromise';
import { assetPinnedLeftArrow, assetPinnedRightArrow } from '../../assets.fwn';
import { isFalsy, onGridScroll } from '../../helpers';
import { filterCurrentInViewport, sortByOtherArray, } from '../../store/tools/utils';
import { usePlayerState } from '../VideoPlayer/hooks/usePlayerState';
import AdBoxGridDesktop from './ads/AdBoxGridDesktop';
import AdBoxGridMobile from './ads/AdBoxGridMobile';
import AdBoxPinnedInline from './ads/AdBoxPinnedInline';
import AdBoxRowDesktop from './ads/AdBoxRowDesktop';
import AdBoxRowMobile from './ads/AdBoxRowMobile';
import { PinnedWrapper } from './mode/PinnedWrapper';
import { AD_GRID_BREAKPOINT, AD_GRID_MOBILE_SPAN, AD_ROW_BREAKPOINT, columnsForWidth, PROMO_URL, ScrollDirectionEnum, } from './GridConstants';
import { Grid, GridInner, GridRowSlot, GridWrapper, LoadMoreButton, RowBottom, RowClaim, RowFooter, RowLogo, RowNavArrow, } from './GridElements';
import Placeholder from './Placeholder';
import PlaylistThumbnail from './PlaylistThumbnail';
import Thumbnail from './Thumbnail';
const GridComponent = ({ appContext, store, largeCarousel, publisherClient, params, placeholders, videos, actions, fetchMoreVideos, hasMore, playlists, }) => {
    const { mode, max_videos, paginate_by, dir } = params;
    const { trackEmbedImpression } = actions;
    const adsEnabled = publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.display_ads_enabled;
    const { t } = useTranslation();
    const gridRef = useRef(null);
    const scrollRef = useRef(null);
    const [[itemsLimit, itemsToRemove], setItemsLimit] = useState([0, 0]);
    const [[width, columns], setColumns] = useState([0, 0]);
    const [pageNumber, setPageNumber] = useState(1);
    const [{ showLeftRowNav, showRightRowNav }, setRowNav] = useState({
        showLeftRowNav: false,
        showRightRowNav: false,
    });
    const [scrolled, setScrolled] = useState(0);
    useLayoutEffect(() => {
        const grid = scrollRef.current;
        if (grid) {
            const handler = onGridScroll(actions);
            if (mode === 'row' || mode === 'pinned') {
                grid.addEventListener('scroll', handler, { passive: true });
                return () => grid.removeEventListener('scroll', handler);
            }
            else {
                window.addEventListener('scroll', handler, { passive: true });
                return () => window.removeEventListener('scroll', handler);
            }
        }
    }, [actions, mode]);
    const trackFeedImpression = useCallback(() => {
        trackEmbedImpression({
            appContext,
            container: gridRef.current,
            eventType: EventType.FW_EMBEDFEED_IMPRESSION,
        });
    }, [trackEmbedImpression]);
    useLayoutEffect(() => {
        const grid = scrollRef.current;
        if (grid && mode === 'row') {
            const scrollListener = (event) => {
                const { scrollLeft, offsetWidth, scrollWidth } = event.target;
                let newState = {
                    showLeftRowNav: false,
                    showRightRowNav: false,
                };
                if (isRTL(dir)) {
                    newState = {
                        showLeftRowNav: !(scrollLeft < scrollWidth - offsetWidth && videos.length > 0),
                        showRightRowNav: !(scrollLeft < 0),
                    };
                }
                else {
                    newState = {
                        showLeftRowNav: scrollLeft > 0,
                        showRightRowNav: scrollLeft < scrollWidth - offsetWidth && videos.length > 0,
                    };
                }
                if (showLeftRowNav !== newState.showLeftRowNav ||
                    (showRightRowNav !== newState.showRightRowNav && !isRTL(dir))) {
                    setRowNav(newState);
                }
                if ((isRTL(dir) && !(showLeftRowNav !== newState.showLeftRowNav)) ||
                    showRightRowNav !== newState.showRightRowNav) {
                    setRowNav(newState);
                }
            };
            scrollListener({ target: grid });
            grid.addEventListener('scroll', scrollListener, {
                passive: true,
            });
            const resizeListener = () => scrollListener({ target: grid });
            window.addEventListener('resize', resizeListener, { passive: true });
            return () => {
                grid.removeEventListener('scroll', scrollListener);
                window.removeEventListener('resize', resizeListener);
            };
        }
    }, [showLeftRowNav, showRightRowNav, videos.length, placeholders, mode]);
    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (grid) {
            if (mode === 'grid') {
                const limit = paginate_by && paginate_by > 0
                    ? pageNumber * (Math.floor(paginate_by / columns) * columns)
                    : max_videos;
                if (limit !== undefined && limit > 0) {
                    const maxVideos = Math.max(columns, limit);
                    const removeExtra = itemsToRemove !== columns ? itemsToRemove : 0;
                    const remove = maxVideos % columns;
                    const add = columns * Math.ceil(maxVideos / columns) - maxVideos;
                    if (remove <= Math.floor(columns / 2)) {
                        setItemsLimit([maxVideos - remove - removeExtra, itemsToRemove]);
                    }
                    else {
                        setItemsLimit([maxVideos + add - removeExtra, itemsToRemove]);
                    }
                }
            }
            let resizer;
            ensureResizeObserver().then((ResizeObserver) => {
                resizer = new ResizeObserver(([entry]) => setColumns([
                    entry.target.clientWidth,
                    columnsForWidth(entry.target.clientWidth),
                ]));
                resizer.observe(grid);
            });
            setColumns([grid.clientWidth, columnsForWidth(grid.clientWidth)]);
            return () => {
                resizer === null || resizer === void 0 ? void 0 : resizer.disconnect();
            };
        }
    }, [
        videos.length,
        columns,
        max_videos,
        mode,
        itemsToRemove,
        pageNumber,
        paginate_by,
    ]);
    const { isPlayingCurrentContent, currentPlayerVideoId } = usePlayerState(appContext, params);
    const navigationForGrid = useMemo(() => (itemsLimit > 0 ? videos.slice(0, itemsLimit) : videos), [itemsLimit, videos]);
    useEffect(() => {
        if (itemsLimit && navigationForGrid.length < itemsLimit && hasMore) {
            fetchMoreVideos(); // TODO: fix reference change from useEmbedFeed
        }
    }, [hasMore, itemsLimit, navigationForGrid]);
    const onScrollClick = (direction) => (event) => {
        var _a;
        event.preventDefault();
        const inViewportIds = store.getState().inViewportIds;
        const ids = sortByOtherArray(filterCurrentInViewport(inViewportIds), videos.map((video) => video.encoded_id));
        let scrollToVideo;
        const moveBackInItems = () => {
            setScrolled(scrolled - 1);
            const lastVisibleId = ids[0];
            const lastVIsibleIndex = videos.findIndex((video) => video.encoded_id === lastVisibleId);
            scrollToVideo = videos[lastVIsibleIndex - 1] || videos[lastVIsibleIndex];
        };
        const moveAheadInItems = () => {
            setScrolled(scrolled + 1);
            const lastVisibleId = ids.slice(-1).pop();
            const lastVIsibleIndex = videos.findIndex((video) => video.encoded_id === lastVisibleId);
            scrollToVideo = videos[lastVIsibleIndex + 1] || videos[lastVIsibleIndex];
        };
        if ((direction === ScrollDirectionEnum.LEFT && !isRTL(dir)) ||
            (direction === ScrollDirectionEnum.RIGHT && isRTL(dir))) {
            moveBackInItems();
        }
        else {
            moveAheadInItems();
        }
        const rootNode = (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.getRootNode();
        const target = rootNode === null || rootNode === void 0 ? void 0 : rootNode.querySelector(`.scrollTo_${scrollToVideo.encoded_id}`);
        if (scrollRef.current && target) {
            if (direction === ScrollDirectionEnum.LEFT) {
                scrollRef.current.scrollLeft =
                    target.offsetLeft +
                        target.offsetWidth -
                        scrollRef.current.clientWidth;
            }
            else {
                scrollRef.current.scrollLeft = target.offsetLeft;
            }
        }
        if (!params.a11y_disable_focus) {
            // ADA: Focus first visible thumb after scrolling
            //  - find focusable thumbnail in viewport (tabindex != -1)
            //  - use setTimeout to overcome smooth scrolling
            setTimeout(() => {
                var _a;
                const firstTappable = (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button.focusable-play-button[tabindex="1"]');
                firstTappable === null || firstTappable === void 0 ? void 0 : firstTappable.focus();
            }, 500);
        }
    };
    /**
     * @param {any} dir:string
     * @returns {boolean}
     */
    const isRTL = (dir = '') => {
        return dir === 'rtl';
    };
    const content = (<GridWrapper borderStyle={params.ui_border_style} ref={gridRef} mode={mode}>
      <GridInner mode={mode}>
        {mode === 'row' && (isRTL(dir) ? !showLeftRowNav : showLeftRowNav) && (<RowNavArrow aria-label={t('Previous')} className="cy-scroll-prev fw-feed-navigation" direction={ScrollDirectionEnum.LEFT} img={assetPinnedLeftArrow} onClick={onScrollClick(ScrollDirectionEnum.LEFT)} tabIndex={3} title={t('Previous')}/>)}
        <Grid columns={columns} mode={mode} ref={scrollRef}>
          {adsEnabled &&
            mode === 'grid' &&
            width &&
            (width > AD_GRID_BREAKPOINT ? (<AdBoxGridDesktop appContext={appContext} params={params}/>) : (<AdBoxGridMobile appContext={appContext} params={params} span={AD_GRID_MOBILE_SPAN} onLoad={() => setItemsLimit([itemsLimit, AD_GRID_MOBILE_SPAN])}/>))}
          {playlists && playlists.length > 0
            ? playlists.map((playlist, i) => {
                return (<Fragment key={playlist.id}>
                    <PlaylistThumbnail actions={actions} appContext={appContext} fetchMoreVideos={mode === 'grid' && paginate_by
                        ? undefined
                        : fetchMoreVideos} hasAutoplay={false} hasPlayingInPlayerOverlay={false} index={i} isLarge={largeCarousel} key={playlist.id} params={params} publisherClient={publisherClient} total={navigationForGrid.length} trackFeedImpression={trackFeedImpression} playlist={playlist}/>
                  </Fragment>);
            })
            : navigationForGrid.map((video, i) => (<Fragment key={video.encoded_id}>
                  {adsEnabled && mode === 'pinned' && i === 2 && (<AdBoxPinnedInline appContext={appContext} params={params}/>)}

                  {mode === 'grid' && i % columns === 0 && (<GridRowSlot name={`grid-row-${i / columns}`}/>)}

                  <Connect mapToProps={({ autoplayIds, playTrigger }) => ({
                    hasAutoplay: autoplayIds[video.encoded_id],
                    videoPlayTrigger: playTrigger[video.encoded_id],
                })}>
                    {({ hasAutoplay, videoPlayTrigger }) => (<Thumbnail actions={actions} appContext={appContext} fetchMoreVideos={mode === 'grid' && paginate_by
                        ? undefined
                        : fetchMoreVideos} hasAutoplay={!isPlayingCurrentContent && hasAutoplay} hasPlayingInPlayerOverlay={currentPlayerVideoId === video.encoded_id} index={i} isLarge={largeCarousel} key={video.encoded_id} params={params} publisherClient={publisherClient} total={navigationForGrid.length} trackFeedImpression={trackFeedImpression} video={video} videoPlayTrigger={videoPlayTrigger}/>)}
                  </Connect>
                </Fragment>))}
          {placeholders.map((_, i) => (<Placeholder isLarge={largeCarousel} key={i} mode={params.mode}/>))}

          {mode === 'grid' && paginate_by && (<>
              <LoadMoreButton className="cy-load-more-button" onClick={() => {
                setPageNumber(pageNumber + 1);
                fetchMoreVideos();
            }}>
                {t('Load More')}
              </LoadMoreButton>
            </>)}
        </Grid>
        {mode === 'row' &&
            (isRTL(dir) ? !showRightRowNav : showRightRowNav) && (<RowNavArrow aria-label={t('Next')} className="cy-scroll-next fw-feed-navigation" direction={ScrollDirectionEnum.RIGHT} img={assetPinnedRightArrow} onClick={onScrollClick(ScrollDirectionEnum.RIGHT)} role="navigation" tabIndex={2} title={t('Next')}/>)}
      </GridInner>

      {adsEnabled && mode === 'row' && width && width > AD_ROW_BREAKPOINT && (<AdBoxRowDesktop appContext={appContext} params={params}/>)}
    </GridWrapper>);
    // Important: Only render when mode is available
    return mode ? (<Fragment>
      {mode === 'pinned' ? (<PinnedWrapper onScrollLeft={onScrollClick(ScrollDirectionEnum.LEFT)} onScrollRight={onScrollClick(ScrollDirectionEnum.RIGHT)} showLeftNav={!!scrolled} title={params.title}>
          {content}
        </PinnedWrapper>) : (content)}

      {mode === 'row' && (<RowBottom center={isFalsy(params.branding) || !!(width && width < AD_ROW_BREAKPOINT)}>
          {!isFalsy(params.branding) && (<a href={PROMO_URL} style={{
                    textDecoration: 'none',
                }} target="_blank" rel="noreferrer">
              <RowFooter>
                <RowLogo className="cy-fwlogo" alt="Firework logo" src={require('@images/logo-v2.svg')} style={{
                    display: 'inline',
                    height: '25px',
                    width: '25px',
                }}/>
                <RowClaim style={{
                    paddingTop: 2,
                }} className="cy-made-in-fw">
                  {t('Made in Firework')}
                </RowClaim>
                <ScreenReaderOnly>{t('Opens a new window')}</ScreenReaderOnly>
              </RowFooter>
            </a>)}

          {adsEnabled && width && width <= AD_ROW_BREAKPOINT && (<AdBoxRowMobile appContext={appContext} params={params}/>)}
        </RowBottom>)}
    </Fragment>) : null;
};
export default GridComponent;
